<template>
	<div
		class="meal-plan-container d-flex justify-content-center align-items-center">
		<GlassCard class="p-4">
			<div v-if="Object.keys(meals).length" class="meal-plan-content">
				<div class="d-flex justify-content-between align-items-center mb-3">
					<h3>{{ title }}</h3>
					<button
						v-if="!viewingMember && isEditable"
						type="button"
						class="btn btn-outline-primary btn-sm"
						@click="showEdit = !showEdit"
						:disabled="!isOnline">
						<i class="bi bi-pencil-square"></i>
					</button>
				</div>
				<!-- Edit Section moved under the title -->
				<div v-if="showEdit" class="edit-section p-2 mb-3">
					<textarea
						class="form-control mb-2"
						placeholder="Edit meal plan..."
						v-model="prompt"></textarea>
					<button
						class="btn btn-primary btn-sm me-2"
						@click="submit"
						:disabled="!isOnline">
						Submit
					</button>
					<button class="btn btn-secondary btn-sm" @click="cancelEdit">
						Cancel
					</button>
				</div>
				<div
					v-for="meal in ['breakfast', 'lunch', 'dinner']"
					:key="meal"
					class="meal mb-3">
					<div
						class="meal-header d-flex justify-content-between align-items-center p-2">
						<span class="meal-title">{{ mealTitles[meal] }}:</span>
						<span class="meal-name">{{ meals[meal].meal }}</span>
						<div>
							<button
								type="button"
								class="btn btn-outline-secondary btn-sm"
								@click="toggleVisibility(meal, 'nutrition')"
								:disabled="!isOnline || meals[meal].isRestaurant">
								<i
									class="bi"
									:class="
										visibility.nutrition[meal]
											? 'bi-eye-slash-fill'
											: 'bi-eye-fill'
									"></i>
							</button>
							<button
								type="button"
								class="btn btn-outline-info btn-sm"
								@click="fetchCookingInstructions(meal)"
								:disabled="!isOnline || meals[meal].isRestaurant">
								<i
									class="bi"
									:class="
										visibility.instructions[meal]
											? 'bi-journal-x'
											: 'bi-journal-text'
									"></i>
							</button>
						</div>
					</div>
					<div v-if="visibility.nutrition[meal]" class="nutrition-info p-2">
						<!-- Nutrition Info Table -->
						<table class="table table-sm" v-if="meals[meal].nutrition">
							<thead>
								<tr>
									<th scope="col">Nutrient</th>
									<th scope="col">Total</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="(total, key) in meals[meal].nutrition" :key="key">
									<td>{{ key }}</td>
									<td>{{ total }}</td>
								</tr>
							</tbody>
						</table>
						<p v-else>N/A</p>
					</div>
					<div
						v-if="visibility.instructions[meal]"
						class="cooking-instructions p-2">
						<markdown :source="meals[meal].cookingInstructions" />
					</div>
				</div>
				<!-- Daily Totals Table -->
				<div class="daily-totals mt-4">
					<h5>Daily Totals</h5>
					<table class="table table-sm">
						<thead>
							<tr>
								<th scope="col">Nutrient</th>
								<th scope="col">Total</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="(total, nutrient) in dailyTotals" :key="nutrient">
								<td>{{ nutrient }}</td>
								<td>{{ total }}</td>
							</tr>
						</tbody>
					</table>
				</div>
				<slot name="additional-content"></slot>
			</div>
			<div v-else class="no-meal-plan text-center">
				<p>Meal plan not available. Please create one.</p>
				<slot name="create-new"></slot>
			</div>
		</GlassCard>
	</div>
</template>

<script setup>
	import { reactive, ref, computed } from "vue"
	import GlassCard from "./GlassCard.vue"
	import Markdown from "./Markdown.vue"
	import { startLoading, stopLoading } from "@/utils/loaderBus"
	import { useTokenStore } from "@/stores/token"
	// import { hasTokenIssue, handleTokenIssue } from "@/utils/tokenIssue"
	import { fetchWithAuth } from "@/utils/api-client"
	import { isOnline } from "@/utils/networkBus"

	const tokenStore = useTokenStore()

	const props = defineProps({
		title: String,
		meals: Object,
		mealTitles: {
			type: Object,
			default: () => ({
				breakfast: "Breakfast",
				lunch: "Lunch",
				dinner: "Dinner",
			}),
		},
		isEditable: Boolean,
		viewingMember: Boolean,
	})

	const emit = defineEmits(["edit"])

	const visibility = reactive({
		nutrition: {},
		instructions: {},
	})
	const showEdit = ref(false)
	const prompt = ref("")

	const submit = () => {
		emit("edit", prompt.value)
		prompt.value = ""
		showEdit.value = false
	}

	const cancelEdit = () => {
		prompt.value = ""
		showEdit.value = false
	}

	const toggleVisibility = (meal, type) => {
		if (visibility[type][meal] === undefined) {
			visibility[type][meal] = false
		}
		visibility[type][meal] = !visibility[type][meal]
	}

	const dailyTotals = computed(() => {
		const totals = {
			Calories: 0,
			Protein: "0 g",
			Carbohydrates: "0 g",
			Fat: "0 g",
			Sodium: "0 mg",
			Fiber: "0 g",
			Sugars: "0 g",
		}

		Object.values(props.meals).forEach(({ nutrition }) => {
			if (nutrition) {
				Object.entries(nutrition).forEach(([nutrient, value]) => {
					if (nutrient === "Calories") {
						totals[nutrient] += parseInt(value || 0)
					} else {
						const unit = totals[nutrient].match(/\D+$/)[0]
						const currentValue = parseInt(totals[nutrient]) || 0
						totals[nutrient] = `${currentValue + parseInt(value || 0)} ${unit}`
					}
				})
			}
		})

		return totals
	})

	const fetchCookingInstructions = async (meal) => {
		if (!props.meals[meal].cookingInstructions) {
			startLoading()
			try {
				const response = await fetchWithAuth("/api/cooking_instructions", {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						// Authorization: `Bearer ${tokenStore.token}`,
					},
					body: JSON.stringify({ meal: props.meals[meal].meal }),
				})
				stopLoading()
				// if (hasTokenIssue(response)) {
				// 	handleTokenIssue(response)
				// } else {
				if (!response.ok) {
					throw new Error("Failed to fetch cooking instructions")
				}
				const data = await response.json()
				// markdownDialog.content = md.render(data.instructions)
				// markdownDialog.show = true
				props.meals[meal].cookingInstructions = data.instructions
				toggleVisibility(meal, "instructions")
				// }
			} catch (error) {
				console.error("Error fetching cooking instructions:", error)
				// markdownDialog.content = "Error loading cooking instructions."
				// markdownDialog.show = true
			}
			stopLoading()
		} else {
			toggleVisibility(meal, "instructions")
		}
	}

	// Expose state to template
	// const state = toRefs(visibility)
</script>

<style scoped>
	.meal-plan-container {
		max-width: 400px;
		width: 100%;
	}
	.meal-header {
		background-color: #f8f9fa;
		border-radius: 0.25rem;
		margin: 0.5rem 0;
		padding: 0.5rem 1rem;
	}
	.meal-title {
		font-weight: bold;
		margin-right: 0.5rem;
	}
	.meal-name {
		flex: 1;
		margin-right: 0.5rem;
		word-break: break-word;
	}
	.btn-sm {
		white-space: nowrap;
	}
	.cooking-instructions p,
	.edit-section {
		margin: 0;
		white-space: pre-line;
	}
	.edit-section textarea {
		width: 100%;
	}
</style>
